import React, { useState, useEffect } from 'react';
import { wardrobeAPI } from '../api';
import { useWebSocket } from '../context/WebSocketContext';

function WardrobePage() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [batchStatus, setBatchStatus] = useState('');
  const { activeJobs } = useWebSocket();

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-reload when batch extraction jobs complete
  useEffect(() => {
    const batchJobs = Array.from(activeJobs.values()).filter(
      job => job.job_type === 'batch_extraction'
    );
    const wasProcessing = batchJobs.some(job => job.status === 'processing');
    const allCompleted = batchJobs.every(job => job.status === 'completed');
    
    // Reload when jobs transition from processing to all completed
    if (wasProcessing && allCompleted) {
      loadItems();
    }
  }, [activeJobs]);

  useEffect(() => {
    filterItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedCategory, searchQuery]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await wardrobeAPI.getItems();
      console.log('Items response:', response.data);
      // API returns array directly, not wrapped in { items: [] }
      const itemsData = Array.isArray(response.data) ? response.data : [];
      setItems(itemsData);
      setFilteredItems(itemsData);
    } catch (err) {
      console.error('Failed to load items:', err);
      setError('Failed to load wardrobe items');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...items];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => 
        item.category && item.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.item_name.toLowerCase().includes(query) ||
        (item.category && item.category.toLowerCase().includes(query)) ||
        (item.tags && item.tags.toLowerCase().includes(query))
      );
    }

    setFilteredItems(filtered);
  };

  const getCategories = () => {
    const categories = new Set();
    items.forEach(item => {
      if (item.category) {
        categories.add(item.category);
      }
    });
    return Array.from(categories).sort();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image');
      return;
    }

    setUploading(true);
    setError('');
    setBatchStatus('');

    try {
      // Always use batch mode - workflow automatically detects all items
      setBatchStatus('📤 Uploading image...');
      const response = await wardrobeAPI.addBatchItems(selectedFile, selectedCategory);
      console.log('Batch upload response:', response.data);
      
      // Backend now returns 202 with processing status
      setBatchStatus('✅ Image uploaded! 🤖 Extracting items in background...');
      
      // Clear form immediately
      setSelectedFile(null);
      setPreviewUrl(null);
      document.getElementById('file-input').value = '';
      setUploading(false);
      
      // Auto-refresh every 3 seconds to show new items as they're processed
      let refreshCount = 0;
      const maxRefreshes = 10; // Max 30 seconds of refreshing
      
      const refreshInterval = setInterval(async () => {
        refreshCount++;
        console.log(`Auto-refresh ${refreshCount}/${maxRefreshes}`);
        await loadItems();
        
        if (refreshCount >= maxRefreshes) {
          clearInterval(refreshInterval);
          setBatchStatus('✅ Processing complete! If items are missing, refresh manually.');
          setTimeout(() => setBatchStatus(''), 5000);
        }
      }, 3000);
      
      // Show processing message for 30 seconds
      setTimeout(() => {
        clearInterval(refreshInterval);
        setBatchStatus('');
      }, 30000);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload image');
      setBatchStatus('');
      setUploading(false);
    }
  };

  // Get items by category/theme
  const getSummerItems = () => {
    return items.filter(item => 
      item.season?.includes('summer') || 
      item.weather?.includes('hot') ||
      item.weather?.includes('warm')
    ).slice(0, 10);
  };

  const getPartyItems = () => {
    return items.filter(item => 
      item.occasion?.includes('party') || 
      item.occasion?.includes('wedding') ||
      item.occasion?.includes('formal') ||
      item.formalities === 'formal'
    ).slice(0, 10);
  };

  const getCasualItems = () => {
    return items.filter(item => 
      item.style === 'casual' || 
      item.occasion?.includes('casual')
    ).slice(0, 10);
  };

  return (
    <div 
      className="container" 
      style={{ 
        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/branding/hero-fashion.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <h1>My Looksy</h1>
      
      {/* Upload Section */}
      <div className="card mb-3">
        <h3>Add New Item</h3>
        
        {error && <div className="error-message">{error}</div>}
        {batchStatus && <div className="success-message">{batchStatus}</div>}
        
        <div className="upload-area">
          <input
            id="camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <input
            id="gallery-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => document.getElementById('camera-input').click()}
              disabled={uploading}
            >
              📷 Take Photo
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => document.getElementById('gallery-input').click()}
              disabled={uploading}
            >
              🖼️ Choose from Gallery
            </button>
          </div>
          
          {previewUrl && (
            <div className="preview-container mt-2">
              <img src={previewUrl} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }} />
            </div>
          )}
          
          {selectedFile && (
            <button 
              className="btn btn-primary mt-2"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? (batchStatus || 'Processing...') : 'Add to Wardrobe'}
            </button>
          )}
        </div>
      </div>

      {/* Featured Carousels */}
      {!loading && items.length > 0 && (
        <div className="featured-section">
          {/* Summer Collection */}
          {getSummerItems().length > 0 && (
            <div className="carousel-section">
              <h3 className="carousel-title">☀️ Summer Collection</h3>
              <div className="carousel-container">
                <div className="carousel-track">
                  {getSummerItems().map((item) => (
                    <div key={item.id} className="carousel-item">
                      <img 
                        src={item.extracted_cloth_url} 
                        alt={item.item_name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect fill="%23f0f0f0" width="150" height="150"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3ELoading...%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="carousel-item-info">
                        <h5>{item.item_name}</h5>
                        {item.colors && item.colors.length > 0 && (
                          <span className="color-tag">{item.colors[0]}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Party & Events */}
          {getPartyItems().length > 0 && (
            <div className="carousel-section">
              <h3 className="carousel-title">✨ Party & Events</h3>
              <div className="carousel-container">
                <div className="carousel-track">
                  {getPartyItems().map((item) => (
                    <div key={item.id} className="carousel-item">
                      <img 
                        src={item.extracted_cloth_url} 
                        alt={item.item_name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect fill="%23f0f0f0" width="150" height="150"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3ELoading...%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="carousel-item-info">
                        <h5>{item.item_name}</h5>
                        {item.formalities && (
                          <span className="formality-tag">{item.formalities}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Casual Wear */}
          {getCasualItems().length > 0 && (
            <div className="carousel-section">
              <h3 className="carousel-title">👕 Casual Wear</h3>
              <div className="carousel-container">
                <div className="carousel-track">
                  {getCasualItems().map((item) => (
                    <div key={item.id} className="carousel-item">
                      <img 
                        src={item.extracted_cloth_url} 
                        alt={item.item_name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect fill="%23f0f0f0" width="150" height="150"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3ELoading...%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="carousel-item-info">
                        <h5>{item.item_name}</h5>
                        {item.style && (
                          <span className="style-tag">{item.style}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Wardrobe Grid */}
      <div className="card">
        <div className="wardrobe-header">
          <h3>Your Items ({filteredItems.length})</h3>
          
          {/* Search Bar */}
          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Search by name, category, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          {/* Category Filter */}
          <div className="category-filter">
            <button
              className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </button>
            {getCategories().map((category) => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        {loading ? (
          <div className="loading-spinner" />
        ) : items.length === 0 ? (
          <p className="text-center">No items yet. Upload your first clothing item!</p>
        ) : filteredItems.length === 0 ? (
          <p className="text-center">No items found matching your filters.</p>
        ) : (
          <div className="grid">
            {filteredItems.map((item) => (
              <div key={item.id} className="wardrobe-item">
                <img 
                  src={item.extracted_cloth_url} 
                  alt={item.item_name}
                  loading="lazy"
                  onError={(e) => {
                    const retryCount = parseInt(e.target.dataset.retryCount || '0');
                    if (retryCount < 3) {
                      // Retry loading the image after a delay
                      e.target.dataset.retryCount = retryCount + 1;
                      setTimeout(() => {
                        e.target.src = item.extracted_cloth_url + '?retry=' + (retryCount + 1);
                      }, 1000 * (retryCount + 1)); // Exponential backoff: 1s, 2s, 3s
                    } else {
                      // After 3 retries, show placeholder
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="14"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                    }
                  }}
                />
                <div className="item-info">
                  <h4>{item.item_name}</h4>
                  {item.category && <span className="category-badge">{item.category}</span>}
                  <p className="text-muted">{new Date(item.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WardrobePage;
